/**
 * @fileoverview UserPanelHeader.jsx v3 - Con Logout funcional
 * - Sin logo de limón
 * - Saludo contextual (Buenos días/tardes/noches)
 * - Botón de Settings para PIN
 * - ✅ NUEVO: Logout funcional
 */

import React from "react";
import { useNavigate } from 'react-router-dom'; // ✅ NUEVO
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast } from 'react-toastify';
import ClimaWidget from '../../components/ClimaWidget';
import { isPlanVigente, tieneAccesoA } from "../../utils/planUtils";
import AvatarWithInitials from '../../components/AvatarWithInitials';
import SettingsModal from './SettingsModal';

// Componente ObjetosPendientes
function ObjetosPendientes({ userId }) {
  const [pendientes, setPendientes] = React.useState([]);
  const [cargando, setCargando] = React.useState(true);
  
  React.useEffect(() => {
    if (!userId) return;
    
    const fetchPendientes = async () => {
      try {
        const token = localStorage.getItem('nfcToken');
        const response = await fetch('/api/objetos/mis-pendientes', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setPendientes(data.data || []);
        }
      } catch (error) {
        console.error('Error cargando pendientes:', error);
      } finally {
        setCargando(false);
      }
    };
    
    fetchPendientes();
  }, [userId]);
  
  if (cargando || pendientes.length === 0) return null;
  
  const activarDirecto = async (objetoId) => {
    try {
      const token = localStorage.getItem('nfcToken');
      const response = await fetch(`/api/objetos/${objetoId}/activar-directo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('¡Objeto activado exitosamente!');
        window.location.reload();
      } else {
        toast.error('Error al activar el objeto');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al activar el objeto');
    }
  };
  
  return (
    <div className="alert alert-info d-flex align-items-center mb-2 rounded-lg"
         style={{
           padding: 'var(--space-2) var(--space-3)',
           borderRadius: 'var(--radius-md)',
           fontSize: 'var(--font-size-sm)'
         }}>
      <i className="bi bi-exclamation-circle-fill me-2" 
         style={{fontSize: 'var(--font-size-xl)'}}></i>
      <div className="flex-grow-1">
        <strong>Tienes {pendientes.length} objeto(s) pendiente(s) de activar</strong>
        <div className="mt-1" style={{display: 'flex', flexDirection: 'column', gap: 'var(--space-1)'}}>
          {pendientes.map(obj => (
            <div key={obj.id} 
                 className="d-flex justify-content-between align-items-center"
                 style={{fontSize: 'var(--font-size-xs)'}}>
              <span>{obj.nombre || 'Sin nombre'} - {obj.tipo}</span>
              <button 
                className="btn btn-sm btn-success transition-base"
                onClick={() => activarDirecto(obj.id)}
                style={{
                  fontSize: 'var(--font-size-xs)',
                  padding: 'var(--space-1) var(--space-2)'
                }}
              >
                Activar ahora
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FILTROS_STORAGE_KEY = 'filtros_expandidos';

export default function UserPanelHeader({
  usuarioSeguro,
  objetos,
  activeFilter,
  setActiveFilter,
  availableCategories,
  menuAbierto,
  setMenuAbierto,
  getCurrentUserId,
  onMisDatosClick,
  onCambiarAvatarClick,
  onVeterinariosClick,
  onProveedoresClick,
  onAgendaClick,
  agendaCount
}) {
  const navigate = useNavigate(); // ✅ NUEVO
  const [showSettings, setShowSettings] = React.useState(false);

  // Estado para filtros colapsables
  const [filtrosExpandidos, setFiltrosExpandidos] = React.useState(() => {
    const saved = localStorage.getItem(FILTROS_STORAGE_KEY);
    if (saved !== null) return saved === 'true';
    // Default: expandido en desktop, colapsado en móvil
    return typeof window !== 'undefined' && window.innerWidth >= 768;
  });

  // Guardar preferencia de filtros
  React.useEffect(() => {
    localStorage.setItem(FILTROS_STORAGE_KEY, String(filtrosExpandidos));
  }, [filtrosExpandidos]);
  
  // ✅ NUEVO: Función para cerrar sesión
  const handleLogout = () => {
    // Limpiar todos los tokens y datos del usuario
    localStorage.removeItem('nfcToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('pinBypass');
    localStorage.removeItem('authToken');

    // ✅ Limpiar sessionStorage (PIN validado)
    sessionStorage.removeItem('pinValidated');
    sessionStorage.removeItem('pinBypassed');

    // Cerrar el menú
    setMenuAbierto(false);

    // Mostrar mensaje de confirmación
    toast.success('Sesión cerrada correctamente');

    // Redirigir al inicio
    navigate('/');
  };

  // Función para obtener saludo contextual
  const obtenerSaludo = () => {
    const hora = new Date().getHours();
    if (hora >= 5 && hora < 12) return "Buenos días";
    if (hora >= 12 && hora < 19) return "Buenas tardes";
    return "Buenas noches";
  };

  // Función para obtener solo el primer nombre
  const obtenerPrimerNombre = (nombreCompleto) => {
    if (!nombreCompleto) return "Usuario";
    return nombreCompleto.trim().split(' ')[0];
  };


  return (
    <>
      <div style={{
        flexShrink: 0,
        backgroundColor: 'var(--white)',
        boxShadow: 'var(--shadow-lg)',
        borderBottom: `2px solid var(--gray-200)`,
        zIndex: 99,
        overscrollBehavior: 'none'
      }}>
        <div className="container-fluid" style={{paddingTop: 'var(--space-2)'}}>
          {/* Objetos Pendientes */}
          <ObjetosPendientes userId={getCurrentUserId()} />
          
          {/* 🎨 FONDO DECORATIVO - Reducido */}
          <div style={{
            position: 'relative',
            background: 'linear-gradient(135deg, #65a30d 0%, #84cc16 50%, #bef264 100%)',
            opacity: 0.4,
            height: '50px',
            borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0'
          }} />
          
          {/* ✨ FILA 1: Saludo + Settings + Avatar */}
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: 'var(--space-2) var(--space-3)',
              position: 'relative',
              marginTop: '-40px',
              zIndex: 1
            }}
          >  
            {/* Saludo Contextual */}
            <div>
              <span style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--gray-900)'
              }}>
                {obtenerSaludo()}, <span style={{color: 'var(--feature-primary)'}}>{obtenerPrimerNombre(usuarioSeguro.nombrePropietario)}</span>
              </span>
            </div>

            {/* Avatar con Menú */}
            <div className="d-flex align-items-center" style={{gap: 'var(--space-3)'}}>

              {/* Avatar con Nombre y Menú */}
              <div style={{position: 'relative', zIndex: 9999}}>
                <div
                  style={{
                    cursor: "pointer",
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-1) var(--space-2)',
                    borderRadius: 'var(--radius-full)',
                    transition: 'var(--transition-fast)',
                    backgroundColor: menuAbierto ? 'var(--gray-100)' : 'transparent'
                  }}
                  onClick={() => setMenuAbierto(!menuAbierto)}
                  title="Menú de usuario"
                  onMouseEnter={(e) => {
                    if (!menuAbierto) e.currentTarget.style.backgroundColor = 'var(--gray-50)';
                  }}
                  onMouseLeave={(e) => {
                    if (!menuAbierto) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <AvatarWithInitials
                    usuario={usuarioSeguro}
                    size={40}
                  />
                  {/* Nombre del usuario - oculto en móvil */}
                  <span
                    className="d-none d-md-inline"
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--gray-800)',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {obtenerPrimerNombre(usuarioSeguro.nombrePropietario)}
                  </span>
                  {/* Flecha hacia abajo */}
                  <i
                    className={`bi bi-chevron-${menuAbierto ? 'up' : 'down'}`}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      transition: 'var(--transition-fast)'
                    }}
                  ></i>
                </div>
                
                {/* Menú Dropdown */}
                {menuAbierto && (
                  <ul 
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'var(--white)',
                      borderRadius: 'var(--radius-lg)',
                      border: `1px solid var(--gray-300)`,
                      padding: 'var(--space-2)',
                      marginTop: 'var(--space-2)',
                      minWidth: '220px',
                      boxShadow: 'var(--shadow-xl)',
                      listStyle: 'none',
                      zIndex: 9999,
                      margin: 0
                    }}
                  >
                    {/* Info del usuario */}
                    <li 
                      style={{
                        padding: 'var(--space-2) var(--space-3)',
                        borderBottom: `1px solid var(--gray-200)`,
                        marginBottom: 'var(--space-2)'
                      }}
                    >
                      <div style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--gray-900)'
                      }}>
                        {usuarioSeguro.nombrePropietario}
                      </div>
                      <div style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-600)',
                        marginTop: 'var(--space-1)'
                      }}>
                        {usuarioSeguro.correoPropietario}
                      </div>
                    </li>
                    
                    {/* Mis Datos */}
                    <li>
                      <button
                        onClick={onMisDatosClick}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-sm)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)',
                          color: 'var(--gray-900)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-person-vcard-fill"></i>
                        Mis Datos
                      </button>
                    </li>
                    
                    {/* Cambiar Avatar */}
                    <li>
                      <button
                        onClick={onCambiarAvatarClick}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-sm)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)',
                          color: 'var(--gray-900)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-person-circle"></i>
                        Cambiar Avatar
                      </button>
                    </li>
                    
                    {/* Veterinarios */}
                    {objetos.some(obj => obj.tipo === 'mascota' && obj.plan === 'premium' && isPlanVigente(obj)) && (
                      <li>
                        <button
                          onClick={onVeterinariosClick}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: 'var(--space-2) var(--space-3)',
                            fontSize: 'var(--font-size-sm)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            transition: 'var(--transition-fast)',
                            color: 'var(--gray-900)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <i className="bi bi-heart-pulse-fill"></i>
                          Veterinarios
                        </button>
                      </li>
                    )}
                    
                    {/* Proveedores - Solo Standard/Premium mascotas con plan vigente */}
                    {objetos.some(o => tieneAccesoA(o, 'proveedores')) && (
                      <li>
                        <button
                          onClick={onProveedoresClick}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: 'var(--space-2) var(--space-3)',
                            fontSize: 'var(--font-size-sm)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            transition: 'var(--transition-fast)',
                            color: 'var(--gray-900)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <i className="bi bi-people-fill"></i>
                          Proveedores
                        </button>
                      </li>
                    )}

                    {/* Buscar Servicios (Localizador) */}
                    <li>
                      <button
                        onClick={() => {
                          navigate('/localizador');
                          setMenuAbierto(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-sm)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)',
                          color: 'var(--gray-900)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-search"></i>
                        Buscar Servicios
                      </button>
                    </li>

                    {/* Seguridad (PIN) */}
                    <li>
                      <button
                        onClick={() => {
                          setShowSettings(true);
                          setMenuAbierto(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-sm)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)',
                          color: 'var(--gray-900)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-gear-fill"></i>
                        Configuracion
                      </button>
                    </li>

                    {/* Divider */}
                    <li>
                      <hr style={{
                        margin: 'var(--space-2) 0',
                        border: 'none',
                        borderTop: `1px solid var(--gray-200)`
                      }} />
                    </li>
                    
                    {/* ✅ Cerrar Sesión - AHORA FUNCIONAL */}
                    <li>
                      <button
                        onClick={handleLogout}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-sm)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          transition: 'var(--transition-fast)',
                          color: 'var(--color-danger)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-danger-light)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-box-arrow-right"></i>
                        Cerrar Sesión
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </div>
          
          {/* ✨ FILA 2: Widget de Clima + Agenda */}
          <div style={{
            padding: 'var(--space-1) var(--space-3)',
            backgroundColor: 'var(--gray-50)',
            borderTop: `1px solid var(--gray-200)`,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <div style={{ flex: 1 }}>
              <ClimaWidget
                usuario={usuarioSeguro}
                tieneMascotas={objetos.some(obj => obj.tipo === 'mascota')}
              />
            </div>
            {onAgendaClick && (
              <button
                onClick={onAgendaClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  padding: 'var(--space-2) var(--space-3)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--white)',
                  color: 'var(--gray-700)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  whiteSpace: 'nowrap',
                  transition: 'var(--transition-fast)',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                  e.currentTarget.style.borderColor = 'var(--gray-400)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--white)';
                  e.currentTarget.style.borderColor = 'var(--gray-300)';
                }}
              >
                <i className="bi bi-calendar3"></i>
                Mi agenda
                {typeof agendaCount === 'number' && agendaCount > 0 && (
                  <span style={{
                    backgroundColor: 'var(--brand-primary)',
                    color: 'var(--gray-900)',
                    borderRadius: 'var(--radius-full)',
                    padding: '0 6px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    lineHeight: '1.6',
                    minWidth: '20px',
                    textAlign: 'center',
                  }}>
                    {agendaCount}
                  </span>
                )}
              </button>
            )}
          </div>
          
          {/* ✨ FILA 3: Filtros (Tabs) - Colapsable */}
          {availableCategories.length > 1 && (
            <div style={{
              backgroundColor: 'var(--gray-50)',
              borderTop: `1px solid var(--gray-200)`,
              transition: 'all 0.2s ease'
            }}>
              {/* Header colapsable */}
              <div
                onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <span style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  <i className={`bi bi-chevron-${filtrosExpandidos ? 'up' : 'down'} me-1`}></i>
                  Filtros ({availableCategories.length + 1} categorías)
                </span>
                {!filtrosExpandidos && activeFilter !== 'todos' && (
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--brand-primary)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}>
                    {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}s
                  </span>
                )}
              </div>

              {/* Contenido de filtros */}
              <div style={{
                maxHeight: filtrosExpandidos ? '200px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.2s ease',
                paddingLeft: 'var(--space-3)',
                paddingRight: 'var(--space-3)',
                paddingBottom: filtrosExpandidos ? 'var(--space-2)' : '0'
              }}>
                <div className="d-flex align-items-center"
                     style={{
                       gap: 'var(--space-2)',
                       flexWrap: 'nowrap',
                       overflowX: 'auto',
                       WebkitOverflowScrolling: 'touch'
                     }}>
                  {/* Tab "Todos" */}
                  <button
                    onClick={() => setActiveFilter('todos')}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semibold)',
                      borderRadius: 'var(--radius-md)',
                      whiteSpace: 'nowrap',
                      transition: 'var(--transition-base)',
                      border: activeFilter === 'todos'
                        ? 'none'
                        : `2px solid var(--gray-300)`,
                      backgroundColor: activeFilter === 'todos'
                        ? 'var(--brand-primary)'
                        : 'var(--white)',
                      color: activeFilter === 'todos'
                        ? 'var(--gray-900)'
                        : 'var(--gray-700)',
                      boxShadow: activeFilter === 'todos'
                        ? '0 2px 8px rgba(132, 204, 22, 0.3)'
                        : 'none',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (activeFilter !== 'todos') {
                        e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                        e.currentTarget.style.borderColor = 'var(--gray-400)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeFilter !== 'todos') {
                        e.currentTarget.style.backgroundColor = 'var(--white)';
                        e.currentTarget.style.borderColor = 'var(--gray-300)';
                      }
                    }}
                  >
                    <i className="bi bi-collection-fill me-1"></i>
                    Todos
                  </button>

                  {/* Tabs por categoría */}
                  {availableCategories.map(category => (
                    <button
                      key={category}
                      onClick={() => setActiveFilter(category)}
                      style={{
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        borderRadius: 'var(--radius-md)',
                        whiteSpace: 'nowrap',
                        transition: 'var(--transition-base)',
                        border: activeFilter === category
                          ? 'none'
                          : `2px solid var(--gray-300)`,
                        backgroundColor: activeFilter === category
                          ? 'var(--brand-primary)'
                          : 'var(--white)',
                        color: activeFilter === category
                          ? 'var(--gray-900)'
                          : 'var(--gray-700)',
                        boxShadow: activeFilter === category
                          ? '0 2px 8px rgba(132, 204, 22, 0.3)'
                          : 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (activeFilter !== category) {
                          e.currentTarget.style.backgroundColor = 'var(--gray-100)';
                          e.currentTarget.style.borderColor = 'var(--gray-400)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (activeFilter !== category) {
                          e.currentTarget.style.backgroundColor = 'var(--white)';
                          e.currentTarget.style.borderColor = 'var(--gray-300)';
                        }
                      }}
                    >
                      {category === 'mascota' ? <i className="bi bi-heart-fill me-1"></i> :
                       category === 'persona' ? <i className="bi bi-person-fill me-1"></i> :
                       <i className="bi bi-key-fill me-1"></i>}
                      {category.charAt(0).toUpperCase() + category.slice(1)}s
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Settings */}
      <SettingsModal
        show={showSettings}
        onHide={() => setShowSettings(false)}
        userId={getCurrentUserId()}
        objetoId={objetos?.[0]?.id || null}
        objetoNombre={objetos?.[0]?.nombre || null}
        objetos={objetos}
      />
    </>
  );
}